import React from 'react';
import { Box, Container } from '@mui/material';
import type { PageProps } from '@qwickapps/react-framework';
import { Markdown, Page } from '@qwickapps/react-framework';

const privacyContent = `# Privacy Policy

**Last Updated:** February 12, 2026

## Our Privacy Commitment

QwickPDF is built with privacy as the foundation. This Privacy Policy explains how we handle your data—or more accurately, how we **don't** handle your data, because everything happens in your browser.

## Data We Collect

### None. Zero. Absolutely Nothing.

QwickPDF operates entirely in your web browser. We do not:
- Collect any personal information
- Track your usage
- Store your documents on our servers
- Upload your content anywhere
- Use cookies for tracking
- Share data with third parties

## How QwickPDF Works

### Client-Side Processing
1. **Your Browser**: All document processing happens in your web browser
2. **Local Storage**: Documents are saved in your browser's local storage only
3. **No Servers**: Your content never leaves your device

### What This Means
- Your documents are private and secure
- Only you have access to your content
- Deleting your browser data removes all documents
- We cannot access, view, or recover your documents

## Technical Details

### Local Storage
- Used to auto-save your work in progress
- Stored only on your device
- Can be cleared at any time via browser settings

### No Analytics
- We do not use Google Analytics or similar services
- We do not track page views, sessions, or user behavior
- Your activity is completely private

### No Cookies
- We do not use tracking cookies
- Only essential cookies for application functionality
- No third-party advertising cookies

## Your Rights

### Complete Control
You have complete control over your data because:
- All data stays on your device
- You can clear data anytime via browser settings
- No account or registration required
- No data retention policies (because we have no data)

### Data Portability
- Export your documents as PDF anytime
- Your markdown files can be uploaded/downloaded freely
- No vendor lock-in

## Third-Party Services

### External Links
QwickPDF may contain links to external websites (like our main website qwickapps.com). These external sites have their own privacy policies, which we encourage you to review.

### No Third-Party Integrations
We do not integrate with third-party services that could access your content.

## Changes to Privacy Policy

We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date.

## Open Source

QwickPDF is built on open-source technology. You can verify our privacy claims by inspecting the code.

## Contact Us

If you have questions about this Privacy Policy or our privacy practices, please visit our contact page:

**QwickApps**
- Contact: [qwickapps.com/pages/contact](https://qwickapps.com/pages/contact)
- Website: [qwickapps.com](https://qwickapps.com)

---

**© 2026 QwickApps. All rights reserved.**

## Summary

**Your Privacy is Guaranteed:**
- ✓ No data collection
- ✓ No tracking
- ✓ No uploads
- ✓ No cookies
- ✓ No third-party sharing
- ✓ Complete privacy

**100% local. 100% private. 100% yours.**
`;

class PrivacyPage extends Page<PageProps> {
  getPageProps(): PageProps {
    return {
      slug: 'privacy',
      title: 'Privacy Policy - QwickPDF',
      description: 'Privacy Policy for QwickPDF - Complete privacy guaranteed',
    };
  }

  renderView() {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          sx={{
            fontSize: '0.95rem',
            '& h1': { color: 'var(--theme-primary)', mt: 0, fontSize: '2rem', fontWeight: 700 },
            '& h2': { color: 'var(--theme-text)', mt: 4, mb: 2, fontSize: '1.5rem', fontWeight: 600 },
            '& h3': { color: 'var(--theme-text)', mt: 3, mb: 1.5, fontSize: '1.25rem', fontWeight: 600 },
            '& p': { mb: 2, lineHeight: 1.7, color: 'var(--theme-text-secondary)' },
            '& ul, & ol': { mb: 2, pl: 3 },
            '& li': { mb: 1, color: 'var(--theme-text-secondary)' },
            '& hr': { my: 4, borderColor: 'var(--theme-divider)' },
            '& strong': { color: 'var(--theme-text)', fontWeight: 600 },
          }}
        >
          <Markdown>{privacyContent}</Markdown>
        </Box>
      </Container>
    );
  }
}

export default PrivacyPage;
